import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, ScrollView, Platform } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';

interface MonthYearPickerProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (month: number, year: number) => void;
  selectedMonth?: number;
  selectedYear?: number;
  webAnchor?: AnchorRect | null;
}

export const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  visible,
  onClose,
  onSelect,
  selectedMonth,
  selectedYear,
  webAnchor,
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  const [viewYear, setViewYear] = useState(selectedYear || currentYear);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Generate years from 2020 to current year + 1
  const years = Array.from({ length: currentYear - 2019 }, (_, i) => 2020 + i).sort((a, b) => b - a);

  const handleMonthSelect = (monthIndex: number) => {
    onSelect(monthIndex + 1, viewYear);
    onClose();
  };

  const handleYearChange = (year: number) => {
    setViewYear(year);
  };

  const isCurrentMonth = (monthIndex: number) => {
    return viewYear === currentYear && monthIndex + 1 === currentMonth;
  };

  const isSelectedMonth = (monthIndex: number) => {
    return viewYear === selectedYear && monthIndex + 1 === selectedMonth;
  };

  if (!visible) return null;

  const content = (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Select Month & Year</Text>
        <TouchableOpacity onPress={onClose} style={styles.closeButton}>
          <SVG_COMPONENTS.CANCEL_X width={20} height={20} />
        </TouchableOpacity>
      </View>

      {/* Year Selector */}
      <View style={styles.yearSection}>
        <Text style={styles.sectionTitle}>Year</Text>
        {Platform.OS === 'web' ? (
          <View style={styles.yearGrid}>
            {years.map((year) => (
              <TouchableOpacity
                key={year}
                style={[
                  styles.yearGridButton,
                  viewYear === year && styles.yearGridButtonSelected,
                ]}
                onPress={() => handleYearChange(year)}
              >
                <Text
                  style={[
                    styles.yearGridButtonText,
                    viewYear === year && styles.yearGridButtonTextSelected,
                  ]}
                >
                  {year}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        ) : (
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.yearScrollContainer}
          >
            {years.map((year) => (
              <TouchableOpacity
                key={year}
                style={[
                  styles.yearButton,
                  viewYear === year && styles.yearButtonSelected,
                ]}
                onPress={() => handleYearChange(year)}
              >
                <Text style={[
                  styles.yearButtonText,
                  viewYear === year && styles.yearButtonTextSelected,
                ]}>
                  {year}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}
      </View>

      {/* Month Grid */}
      <View style={styles.monthSection}>
        <Text style={styles.sectionTitle}>Month</Text>
        <View style={styles.monthGrid}>
          {months.map((month, index) => (
            <TouchableOpacity
              key={month}
              style={[
                styles.monthButton,
                isCurrentMonth(index) && styles.monthButtonCurrent,
                isSelectedMonth(index) && styles.monthButtonSelected,
              ]}
              onPress={() => handleMonthSelect(index)}
            >
              <Text style={[
                styles.monthButtonText,
                isCurrentMonth(index) && styles.monthButtonTextCurrent,
                isSelectedMonth(index) && styles.monthButtonTextSelected,
              ]}>
                {month.substring(0, 3)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // Desktop/web anchored dropdown
  if (Platform.OS === 'web' && webAnchor) {
    return (
      <AnchoredDropdown isOpen={true} anchor={webAnchor} width={400} maxHeight={560} onRequestClose={onClose}>
        {content}
      </AnchoredDropdown>
    );
  }

  // Mobile modal
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        {content}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  container: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  closeButton: {
    padding: 4,
  },
  yearSection: {
    padding: 20,
    paddingBottom: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 12,
    textTransform: 'uppercase',
  },
  yearScrollContainer: {
    paddingHorizontal: 4,
  },
  yearGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  yearGridButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    minWidth: 82,
    alignItems: 'center',
  },
  yearGridButtonSelected: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
  },
  yearGridButtonText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '500',
  },
  yearGridButtonTextSelected: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  yearButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginHorizontal: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  yearButtonSelected: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
  },
  yearButtonText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '500',
  },
  yearButtonTextSelected: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  monthSection: {
    padding: 20,
    paddingTop: 10,
  },
  monthGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  monthButton: {
    width: '30%',
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  monthButtonCurrent: {
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    borderWidth: 2,
  },
  monthButtonSelected: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
  },
  monthButtonText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '500',
  },
  monthButtonTextCurrent: {
    color: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    fontWeight: '600',
  },
  monthButtonTextSelected: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  footer: {
    padding: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
    alignItems: 'center',
  },
  cancelButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BUTTON.PRIMARY_BACKGROUND,
  },
  cancelButtonText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    fontWeight: '500',
  },
});
